import { useTranslation } from "@i18n";
import React, { useEffect, useRef, useState } from "react";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { getAllUsersInSpace } from "@Actions/projectUsers";
import { useDispatch, useSelector } from "react-redux";
import axiosWrapper from "@Services/axiosWrapper";
import type { SpacesDataResponse } from "@ServerTypes/spaces/space.schema";
import { getBackendUrl } from "@Utils";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { getInspectionUrl, formCategories, defaultModuleNamesMapper } from "@Helpers";
import { useNavigate, useParams } from "react-router-dom";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { formCategoryObject } from "@Features/portal-settings";
import { Box } from "@mui/material";
import { TaskInspectionAndProgressForm as TasksForms } from "@Features/forms";
import useCreateInspection from "./useCreateInspection";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { MileStones } from "@Features/contracts";
import ContractTasks from "./contractTaskGrid";
import { getContractDetails } from "@Features/contracts/state/contracts.actions";
import DatePickerComponent from "@CubeComponents/Mui_Form/DatePickerComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import TreeSelectDropDown from "@CubeComponents/html/TreeDropDown";
import FormFlowCreationNew from "./formFlowCreationNew";
import Text from "@CubeComponents/html/Text/Text";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { RootState } from "@Reducers/index";
import _ from "lodash";

function InspectionCreation(props: any) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams() as { projectId: string; contractId?: string };
  const milestoneRef: any = useRef<any>(null);
  const treeGrid: any = useRef<any>(null);
  const formsRef: any = useRef<any>(null);
  const dispatch = useDispatch();
  const { getProjectSettingObject, portalData, customModules, contractDetails } = useSelector(
    (state: RootState) => state,
  );

  const {
    title,
    description,
    dueDate,
    setDueDate,
    selectedForm,
    setSelectedForm,
    spaceId,
    setSpaceId,
    selectedFormFlow,
    setSelectedFormFlow,
    handleCancelFormAttachment,
    handleFormFlowCreate,
    attachEntity,
    setAttachEntity,
    handleCreateClick,
    progressUpdateEntityCondition,
    inspectionEntityCondition,
    isRustomjeePortal,
    isMPMRCLPortal,
    allowedAttachmentConstants,
    setRememberUserSettings,
    rememberUserSettings,
    templateDataSource,
    showPreview,
    setShowPreview,
    showCreateFlowModal,
    setShowCreateFlowModal,
    formFlowOptions,
    handleSelectTemplate,
    handleSetDescription,
    handleSetTitle,
    showLoader,
    formFlowFilter,
    handleFlowFilterChange,
    handleClearFlowFilter,
  }: any = useCreateInspection({
    mobile: false,
    projectId: props.projectId,
    ...props,
    formCategoryObject,
    getInspectionUrl,
    navigate,
    contractId: params.contractId,
    resourceType: props.showFormFlowCreationModal.category,
    portalData,
    getContractDetails,
  });

  const [_spacesOptions, setSpacesOptions] = useState<any[]>([]);

  useEffect(() => {
    const fetchSpaces = async () => {
      if (props.projectId) {
        try {
          const response = await axiosWrapper.post<SpacesDataResponse>(`${getBackendUrl()}/spaces/spacesData`, {
            value: { projectId: props.projectId },
          });
          if (response.data?.result) {
            setSpacesOptions((response.data as any).result);
          }
        } catch (error) {
          console.error("Error fetching spaces:", error);
          Promise.reject(error);
        }
      }
    };
    fetchSpaces();
  }, [props.projectId]);

  useEffect(() => {
    if (spaceId) {
      dispatch(
        getAllUsersInSpace({
          spaceId,
        }),
      );
    }
  }, [spaceId]);

  const handleChange = (args: any) => {
    if (args?.target?.name == "title") {
      handleSetTitle(args?.target?.value);
    } else if (args?.target?.name == "description") {
      handleSetDescription(args?.target?.value);
    }
  };

  const formsHeaderContent = () => {
    const categoryObj = customModules.find(
      (module: any) => module.resourceType == props.showFormFlowCreationModal.category,
    );
    const formHeader =
      categoryObj?.formHeader ?? formCategoryObject[props.showFormFlowCreationModal.category]?.formHeader ?? "";
    return (
      <div className="flex w-full gap-2 items-center justify-between">
        <span>{`Create ${formHeader}`}</span>
        {props.source == "project" && (
          <SelectSpaces
            moduleName={props.showFormFlowCreationModal.category}
            data-testid="spaceDropdownField"
            allowBorder={true}
            projectId={props.projectId}
            selectedSpaceCallback={(params: any) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    );
  };

  const handleFormFlowSelection = (args: any) => {
    setSelectedFormFlow(args);
    if (args?.formFlow?.length == 0) {
      dispatch(
        showToastNotification({
          content: t("This formFlow's flow is not defined"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  const headerTemplateForFormFlow = () => {
    const filterButtonSx = (isActive: boolean) => ({
      borderRadius: "0.5rem",
      padding: "0.375rem 1rem",
      boxShadow: "none",
      color: "var(--primary-text)",
      ...(isActive
        ? {
            background: "var(--light-gray)",
            border: "1px solid transparent",
            "&:hover": { background: "#D8DEEC" },
          }
        : {
            background: "var(--surface-white)",
            border: "1px solid var(--lighter-gray)",
            "&:hover": { background: "var(--surface-hover-light)" },
          }),
    });

    return (
      <div className="flex border-b border-b-button_light !gap-3 p-3 -mt-3 -mb-3 w-full items-center">
        <Text className="font-semibold">{t("I'm part of")}</Text>
        <ButtonComponent
          onClick={() => handleFlowFilterChange("anyLevel")}
          sx={filterButtonSx(formFlowFilter === "anyLevel")}
        >
          {t("Any Level")}
        </ButtonComponent>
        <ButtonComponent
          onClick={() => handleFlowFilterChange("firstLevel")}
          sx={filterButtonSx(formFlowFilter === "firstLevel")}
        >
          {t("First Level")}
        </ButtonComponent>
      </div>
    );
  };

  const emptyListTemplate = () => (
    <div className="flex flex-col items-center justify-center gap-4 px-4 py-8">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-light_gray">
        <IconComponent name="Filter" width="1.75rem" height="1.75rem" color="var(--primary-text)" />
      </span>
      <Text className="text-text_light text-center">{t("No form flows match this filter")}</Text>
      <ButtonComponent
        buttonType={BUTTON_TYPES.TEXT_BUTTON}
        onClick={handleClearFlowFilter}
        sx={{ fontWeight: 600, color: "var(--primary-text)" }}
      >
        {t("Clear filter")}
      </ButtonComponent>
    </div>
  );

  const formsModelContent = () => {
    const categoryObj = customModules.find(
      (module: any) => module.resourceType == props.showFormFlowCreationModal.category,
    );

    const formHeader =
      categoryObj?.formHeader ?? formCategoryObject[props.showFormFlowCreationModal.category]?.formHeader ?? "";
    return (
      <div className="flex flex-col gap-6">
        <div className="flex gap-6 items-center">
          <span className="font-semibold w-3/12 gap-1 items-center flex">
            <Text className="font-semibold">{t("Template")}</Text>
            <span className="text-2xl font-light text-red-500">*</span>
          </span>
          <div className="w-9/12 flex gap-6">
            <div className="w-3/5">
              <TreeSelectDropDown
                placeholder={t("Select Template")}
                treeData={(templateDataSource || []).map((item: any) => ({
                  ...item,
                  id: item._id,
                  label: item.name,
                  parentId: item.parentId ?? null,
                }))}
                value={selectedForm?.name ?? ""}
                onSelectionChange={handleTemplateSelect}
                disabledParentSelection
              />
            </div>
            <div className="h-8 w-8 p-1 items-center rounded-sm bg-button_light">
              <IconComponent
                isEnabled={!!selectedForm._id}
                className="cursor-pointer"
                onClick={() => setShowPreview("Template")}
                name="preview"
                width="1.5rem"
                height="1.5rem"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-6 items-center">
          <span className="font-semibold w-3/12 gap-1 items-center flex">
            <Text className="font-semibold">{t("Form Flow")}</Text>
            <span className="text-2xl font-light text-red-500">*</span>
          </span>
          <div className="w-9/12 flex items-center gap-6">
            <div className="w-3/5">
              <SelectDropDown
                headerTemplate={headerTemplateForFormFlow}
                emptyListTemplate={emptyListTemplate}
                placeholder={t("Select form flow")}
                onChange={handleFormFlowSelection}
                value={selectedFormFlow._id}
                disabled={!selectedForm._id}
                buttonType={BUTTON_TYPES.BORDER_BUTTON}
                fields={{
                  text: "name",
                  value: "_id",
                }}
                options={formFlowOptions.filteredOptions}
                buttonWidth="100%"
                popperWidth={400}
              />
            </div>
            <div className="h-8 w-8 p-1 items-center rounded-sm bg-button_light">
              <IconComponent
                isEnabled={!!selectedFormFlow._id}
                className="cursor-pointer"
                onClick={() => setShowPreview("FormFlow")}
                name="preview"
                width="1.5rem"
                height="1.5rem"
              />
            </div>
            {getProjectSettingObject.formFlow?.add?.enable && (
              <>
                <div>{"or"}</div>
                <div>
                  <ButtonComponent
                    disabled={!selectedForm._id || !spaceId}
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleFormFlowCreate}
                  >
                    <div className="flex items-center gap-1">
                      <IconComponent isEnabled={!!selectedForm._id} name="add" width="1rem" height="1rem" />
                      <Text>{t("New Flow")}</Text>
                    </div>
                  </ButtonComponent>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-6 items-center">
          <span className="font-semibold w-3/12 gap-1 items-center flex">
            <Text>{t("Title")}</Text>
            <span className="text-2xl font-light text-red-500">*</span>
          </span>
          <span className="w-9/12">
            <TextFieldComponent
              name="title"
              placeholder={t("Title")}
              showClearButton
              value={title}
              onChange={handleChange}
            />
          </span>
        </div>
        <div className="flex gap-6 items-center">
          <Text className="font-semibold w-3/12">{t("Description")}</Text>
          <span className="w-9/12">
            <TextFieldComponent
              name="description"
              placeholder={t("Description")}
              showClearButton
              value={description}
              multiline={true}
              rows={3}
              onChange={handleChange}
            />
          </span>
        </div>
        {!isMPMRCLPortal && (
          <div className="flex gap-6 items-center">
            <div className="font-semibold w-3/12 gap-1 items-center flex">{t("Due Date")}</div>
            <div className="w-9/12">
              <div className="w-3/5">
                <DatePickerComponent
                  min={new Date()}
                  value={dueDate ? new Date(dueDate) : undefined}
                  id="datetimepicker"
                  width="100%"
                  onChange={(args: any) => {
                    if (args.value && new Date(args.value).getTime() > Date.now()) {
                      setDueDate(new Date(args.value).setHours(23, 59, 0, 0));
                    } else {
                      setDueDate(null);
                    }
                  }}
                  placeholder={t("Enter Due Date")}
                  format="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>
        )}
        {props.source == "contract" && (progressUpdateEntityCondition || inspectionEntityCondition) && (
          <div className="flex gap-6 items-center">
            <span className="font-semibold w-3/12">{`${formHeader} based on`}</span>
            <span className="flex w-9/12 justify-between">
              <span className="w-full flex gap-5">
                {progressUpdateEntityCondition && (
                  <RadioButton
                    label="Progress Updates"
                    onChange={() =>
                      setAttachEntity({
                        name: formCategories.progressUpdates,
                      })
                    }
                    checked={
                      attachEntity.name === formCategories.progressUpdates ||
                      attachEntity[formCategories.progressUpdates]?.length > 0
                    }
                  />
                )}

                {inspectionEntityCondition && (
                  <RadioButton
                    label={isRustomjeePortal ? t("Work Done") : t("Inspections")}
                    onChange={() => setAttachEntity({ name: formCategories.inspection })}
                    checked={
                      attachEntity.name === formCategories.inspection ||
                      attachEntity[formCategories.inspection]?.length > 0
                    }
                  />
                )}
              </span>
              <Box onClick={handleClearClick} className="bg-button_light rounded-2xl h-6 ps-2 pe-2 cursor-pointer">
                <Text>{t("Clear")}</Text>
              </Box>
            </span>
          </div>
        )}
        {props.showFormFlowCreationModal.category !== formCategories.changeOrder && (
          <div>
            <CheckBoxComponent
              onChange={(args: any) => setRememberUserSettings(args.checked)}
              checked={rememberUserSettings}
              label="Remember the configuration being applied in this series of steps for subsequent form(s) creation"
            />
          </div>
        )}
      </div>
    );
  };

  const handleTemplateSelect = (params: any) => {
    if (!params) {
      setSelectedForm({});
      return;
    }
    if (!params.parentId) {
      setSelectedForm({});
      dispatch(
        showToastNotification({
          content: t("Please select a template and not template type to continue"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (params) {
      handleSelectTemplate(params);
    }
  };

  const handleClearClick = () => {
    setAttachEntity({ name: "" });
  };

  const formsFooterContent = () => {
    let isCreateDisable = !title || !selectedForm._id || !selectedFormFlow._id || !spaceId;
    return (
      <div className=" flex gap-4">
        <ButtonComponent
          disabled={isCreateDisable}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleCreateClick}
          loading={showLoader}
        >
          <Text>{t("Create")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCancelFormAttachment}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };

  const getInspectionFormFlow = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={formsFooterContent()}
      width="60%"
      headerTitle={formsHeaderContent()}
      onCancel={handleCancelFormAttachment}
      isVisible={props.showFormFlowCreationModal.status}
      modelContent={formsModelContent()}
    />
  );

  const closeAttachEntityModal = () => {
    setAttachEntity({ name: "budget" });
  };

  const handleAttachEntityClick = () => {
    if (attachEntity.name == "tasks") {
      const slick = treeGrid.current?.slickGrid;
      const selectedTasks = (slick?.getSelectedRows?.() ?? []).map((data: any) => slick?.getDataItem?.(data));
      if (!selectedTasks.length) {
        dispatch(
          showToastNotification({
            content: t("Select activities first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
        return;
      }
      const taskIds = selectedTasks.map((task: any) => task._id);
      setAttachEntity({ name: "", [attachEntity.name]: taskIds });
    } else if (attachEntity.name == formCategories.progressUpdates) {
      const selectedProgressUpdates = formsRef.current?.dataView?.getAllSelectedItems() || [];
      if (selectedProgressUpdates.length) {
        const progressUpdateIds = selectedProgressUpdates.map((form: any) => form._id);
        setAttachEntity({ name: "", [attachEntity.name]: progressUpdateIds });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select Progress Updates(s) first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    } else if (attachEntity.name == formCategories.inspection) {
      const selectedInspection = formsRef.current?.dataView?.getAllSelectedItems() || [];
      if (selectedInspection.length) {
        const inspectionIds = selectedInspection.map((form: any) => form._id);
        setAttachEntity({ name: "", [attachEntity.name]: inspectionIds });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select Inspections(s) first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    } else if (attachEntity.name == "milestones") {
      const selectedMileStones = milestoneRef.current?.dataView?.getAllSelectedItems?.() || [];
      if (selectedMileStones.length) {
        const milestoneIds = selectedMileStones.map((mileStone: any) => mileStone._id);
        setAttachEntity({ name: "", [attachEntity.name]: milestoneIds });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select Milestone(s) first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    }
  };

  const attachEntityFooter = () => {
    const isModuleDisabled = params.contractId && !contractDetails?.modulesEnabled[attachEntity.name];
    return (
      <div className="flex gap-4">
        <ButtonComponent
          disabled={isModuleDisabled}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleAttachEntityClick}
        >
          <Text>{t("Attach")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeAttachEntityModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };
  const attachEntityHeader = () => (
    <div className="w-full flex justify-between rounded-md">
      <div className="flex flex-col gap-1">
        <div>{`Attach ${defaultModuleNamesMapper[attachEntity.name]}`}</div>
        <span className="text-sm font-normal">{`All the ${defaultModuleNamesMapper[attachEntity.name]} related to this Contract`}</span>
      </div>
    </div>
  );
  const attachEntityContent = () => {
    const AnyTasksForms: any = TasksForms;
    const AnyMilestone: any = MileStones;
    const AnyContractTasks: any = ContractTasks;
    if (attachEntity.name === "tasks") {
      return <AnyContractTasks ref={treeGrid} contractId={params.contractId} />;
    }
    if (attachEntity.name === formCategories.progressUpdates) {
      return (
        <div className=" h-full flex overflow-auto">
          <AnyTasksForms
            showOnlyApproved
            source="contract"
            ref={formsRef}
            showOnlyGrid
            formCategory={formCategories.progressUpdates}
            projectId={params.projectId}
          />
        </div>
      );
    }
    if (attachEntity.name === formCategories.inspection) {
      return (
        <div className="m-6 h-full flex overflow-auto">
          <AnyTasksForms
            showOnlyApproved
            source="contract"
            ref={formsRef}
            showOnlyGrid
            formCategory={formCategories.inspection}
            projectId={params.projectId}
          />
        </div>
      );
    }
    if (attachEntity.name === "milestones") {
      return (
        <div className="m-6 h-full flex overflow-auto">
          <AnyMilestone showOnlyGrid ref={milestoneRef} contractId={params.contractId} projectId={params.projectId} />
        </div>
      );
    }
  };

  const previewContent = () => {
    if (showPreview == "Template") {
      return <FormPreview formData={selectedForm} />;
    } else if (showPreview == "FormFlow") {
      return (
        <FormFlowCreationNew
          showLoader={showLoader}
          formFlowId={selectedFormFlow._id}
          spaceId={spaceId}
          projectId={props.projectId}
          previewMode
        />
      );
    }
    return <></>;
  };
  return (
    <div>
      {props.showFormFlowCreationModal.status && getInspectionFormFlow()}
      {allowedAttachmentConstants.includes(attachEntity.name) && (
        <ModalStateDialogComponent
          allowModalPadding={false}
          footerContent={attachEntityFooter()}
          width="80%"
          height="80%"
          headerTitle={attachEntityHeader()}
          onCancel={closeAttachEntityModal}
          isVisible
          modelContent={attachEntityContent()}
        />
      )}
      {showPreview && (
        <ModalStateDialogComponent
          footerContent={
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={() => setShowPreview(null)}>
              {t("Close")}
            </ButtonComponent>
          }
          allowModalPadding={false}
          width="80%"
          height="80%"
          headerTitle={<>{`${showPreview} | ${t("Preview")}`}</>}
          onCancel={() => setShowPreview(null)}
          isVisible
          modelContent={previewContent()}
        />
      )}
      {showCreateFlowModal && (
        <ModalStateDialogComponent
          allowModalPadding={false}
          width="80%"
          height="90%"
          headerTitle={<>{t("New Flow")}</>}
          onCancel={() => setShowCreateFlowModal(false)}
          isVisible
          modelContent={
            <FormFlowCreationNew
              showLoader={showLoader}
              handleCancelFormAttachment={() => setShowCreateFlowModal(false)}
              handleCreateClick={handleCreateClick}
              spaceId={spaceId}
              source="FORM_CREATION"
              projectId={props.projectId}
              selectedTemplate={selectedForm}
            />
          }
        />
      )}
    </div>
  );
}
export default InspectionCreation;
